import React, { useEffect, useState, useContext } from 'react';
import {
  Card,
  Table,
  Typography,
  Button,
  DatePicker,
  message,
} from 'antd';
import dayjs from 'dayjs';
import APIUtils from '../../api/APIUtils';
import WebLinks from '../../api/WebLinks';
import { CommonContext } from '../../Context';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const HIRE_INTELLIGENCE_GREEN = '#00B2A8';
const tableHeaderStyle = {
  backgroundColor: HIRE_INTELLIGENCE_GREEN,
  color: '#ffffff',
};
const getLandingPageColumnTitle = (columnKey = '') => {
  const normalizedKey = String(columnKey).trim();
  if (!normalizedKey) {
    return 'Landing Page Views';
  }
  if (normalizedKey.toLowerCase() === 'views') {
    return 'Landing Page Views';
  }
  return String(columnKey)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getCareerSiteColumnTitle = (pageType = '') => {
  const normalizedKey = String(pageType).trim().toLowerCase();
  const pageTypeLabels = {
    home: 'Home Page Views',
    home_page: 'Home Page Views',
    search: 'Search Page Views',
    search_page: 'Search Page Views',
    landing: 'Landing Page Views',
    landing_page: 'Landing Page Views',
    job: 'Job Page Views',
    other: 'Other Views',
    other_page: 'Other Views',
    total: 'Total Views',
    total_views: 'Total Views',
  };
  return pageTypeLabels[normalizedKey] || String(pageType)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const getComparableValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const numericCandidate = trimmed.replace(/[$,%\s]/g, '');
    if (!Number.isNaN(Number(numericCandidate)) && numericCandidate !== '') {
      return Number(numericCandidate);
    }

    return trimmed.toLowerCase();
  }

  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }

  return String(value).toLowerCase();
};

export const compareValues = (left, right) => {
  const a = getComparableValue(left);
  const b = getComparableValue(right);

  if (a === null && b === null) {
    return 0;
  }
  if (a === null) {
    return 1;
  }
  if (b === null) {
    return -1;
  }

  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }

  return String(a).localeCompare(String(b));
};

const LandingPage = () => {
  const DATE_RANGE_STORAGE_KEY = 'landingPageDateRange';
  const [careersSiteColumns, setCareersSiteColumns] = useState([]);
  const [careersSiteData, setCareersSiteData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [landingData, setLandingData] = useState([]);
  const [careersSiteTotals, setCareersSiteTotals] = useState(null);
  const [dateRange, setDateRange] = useState(() => {
    const storedRange = sessionStorage.getItem(DATE_RANGE_STORAGE_KEY);
    if (storedRange) {
      try {
        const parsedRange = JSON.parse(storedRange);

        if (
          Array.isArray(parsedRange) &&
          parsedRange.length === 2 &&
          parsedRange[0] &&
          parsedRange[1]
        ) {
          return [
            dayjs(parsedRange[0]).startOf('month'),
            dayjs(parsedRange[1]).startOf('month'),
          ];
        }
      } catch (error) {
        console.error('Invalid stored date range:', error);
      }
    }

    return [
      dayjs().subtract(1, 'month').startOf('month'),
      dayjs().startOf('month'),
    ];
  });
  const commonContext = useContext(CommonContext);
  const buyerId = commonContext?.common?.buyerId;
  const masterBuyerId = sessionStorage.getItem('isMasterBuyer');
  const landingBuyerId =
    masterBuyerId && masterBuyerId !== 'undefined' && masterBuyerId !== 'null'
      ? masterBuyerId
      : buyerId;

  useEffect(() => {
    // if (!landingBuyerId) return;
    const init = async () => {
      try {
        // fetch buyer details so careerSiteDomain is set before other calls
        const response = await APIUtils.apiCall('GET', WebLinks.GetBuyerDetails + landingBuyerId);
        if (!response.isCatch && response.resultObj?.statusCode === '2000') {
          const domain = response.resultObj?.data?.careerSiteDomain;
          if (domain) {
            sessionStorage.setItem('careerSiteDomain', domain);
          }else{
            sessionStorage.removeItem('careerSiteDomain');
          }
        }
      } catch (e) {
        console.error('buyer details error', e);
      }
      // now call existing data loads
      fetchCareerSiteSummary(dateRange);
      getData(dateRange);
    };
    init();
  }, [landingBuyerId]);

  const formatColumnTitle = (value = '') => {
    return String(value)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };
  
  const getData = async (range) => {
    setLoading(true);
    try {
      const response = await APIUtils.apiCall(
        'GET',
        WebLinks.GetLandingPageSettings +
          'buyerId=' +
          landingBuyerId +
          '&limit=25&pageNo=1&orderBy=occupation&sortDesc=false',
        null,
        true,
        false
      );
      if (!response.isCatch && response.resultObj.statusCode === '2000') {
        const records = response?.resultObj?.data || [];
        setLandingData(records);
        fetchLandingAnalytics(records,range);
      } else {
        setLandingData([]);
      }
    } catch (err) {
      console.error('getData error', err);
      setLandingData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLandingAnalytics = async (records = [],range) => {
    if (!records || records.length === 0) return;
    const careerSiteDomain = sessionStorage.getItem('careerSiteDomain');
    const site = careerSiteDomain ? careerSiteDomain.replace(/^https?:\/\//, '').replace(/\/$/, '') : '';
    const start = range[0];
    const end = range[1];
    const results = [];
    for (const rec of records) {
      const landingValue = rec?.occupation || rec?.occupation?.toString() || '';
      const params = new URLSearchParams({
        site,
        landing: landingValue,
        year_start: start.format('YYYY'),
        month_start: start.format('M'),
        year_end: end.format('YYYY'),
        month_end: end.format('M'),
      });
      try {
        const res = await fetch(
          `${WebLinks.GetLandingPage}?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${process.env.REACT_APP_LANDING_PAGE_API_KEY}`,
            },
          }
        );
        if (res.ok) {
          const json = await res.json();
          rec.analytics = json;
          results.push({ id: rec.id, success: true });
        } else {
          rec.analytics = { error: `status ${res.status}` };
          results.push({ id: rec.id, success: false });
        }
      } catch (e) {
        rec.analytics = { error: e.message };
        results.push({ id: rec.id, success: false });
      }
    }
    setLandingData([...records]);
    return results;
  };
  
  const getRowLabelKey = (row = {}) => {
    return Object.keys(row).find(
      (key) => key !== 'counts' && key !== 'total'
    );
  };

  const createLandingReportColumns = (rows = [], totals = null) => {
    if ((!rows || rows.length === 0) && !totals) return [];

    const rowKeys = rows?.length > 0 ? Object.keys(rows[0]) : [];
    const totalKeys = totals ? Object.keys(totals) : [];

    const keys = [...new Set([...rowKeys, ...totalKeys])]
      .filter(
        (key) =>
          key !== 'key' &&
          key !== 'campaign' &&
          key.toLowerCase() !== 'applications'
      );

    return keys.map((k, index) => ({
      title: getLandingPageColumnTitle(k),
      dataIndex: k,
      key: k,
      align: index === 0 ? 'left' : 'center',
      sorter: (a, b) => compareValues(a?.[k], b?.[k]),
      onHeaderCell: () => ({ style: tableHeaderStyle }),
    }));
  };

  const createCareerSiteColumns = (rows = [], pageTypes = []) => {
    const firstRow = rows[0] || {};
    const rowLabelKey = getRowLabelKey(firstRow) || 'source';

    const dynamicColumns = pageTypes.map((pageType) => ({
      title: getCareerSiteColumnTitle(pageType),
      dataIndex: pageType,
      key: pageType,
      align: 'center',
      sorter: (a, b) =>
        compareValues(a?.[pageType], b?.[pageType]),
      onHeaderCell: () => ({ style: tableHeaderStyle }),
    }));
    return [
      {
        title: formatColumnTitle(rowLabelKey),
        dataIndex: rowLabelKey,
        key: rowLabelKey,
        align: 'left',
        sorter: (a, b) =>
          compareValues(
            a?.[rowLabelKey],
            b?.[rowLabelKey]
          ),
        onHeaderCell: () => ({ style: tableHeaderStyle }),
      },
      ...dynamicColumns,
      {
        title: 'Total Views',
        dataIndex: 'total',
        key: 'total',
        align: 'center',
        sorter: (a, b) =>
          compareValues(a?.total, b?.total),
        onHeaderCell: () => ({ style: tableHeaderStyle }),
      },
    ];
  };

  const getCareerSiteSummary = async (range) => {
    const careerSiteDomain = sessionStorage.getItem('careerSiteDomain');
    if (!careerSiteDomain) {
      throw new Error('Career site domain not found in session storage.');
    }
    const site = careerSiteDomain
      .replace(/^https?:\/\//, '')
      .replace(/\/$/, '');

    const startDate = range[0];
    const endDate = range[1];

    const params = new URLSearchParams({
      site,
      year_start: startDate.format('YYYY'),
      month_start: startDate.format('M'),
      year_end: endDate.format('YYYY'),
      month_end: endDate.format('M'),
    });
    const response = await fetch(
      `${WebLinks.GetCarrierData}?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_LANDING_PAGE_API_KEY}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch career site summary. Status: ${response.status}`
      );
    }

    return response.json();
  };

  const fetchCareerSiteSummary = async (range) => {
    if (!range || range.length !== 2) {
      return;
    }

    try {
      setLoading(true);

      const response = await getCareerSiteSummary(range);
      const summary = response?.summary;

      if (!summary) {
        throw new Error('Invalid API response.');
      }

      const pageTypes = summary?.page_types || [];
      const rows = summary?.rows || [];
      if (rows.length === 0) {
        setCareersSiteColumns([]);
        setCareersSiteData([]);
        setCareersSiteTotals(null);
        return;
      }
      const columnTotals = summary?.column_totals || {};
      const grandTotal = summary?.grand_total || 0;
      const firstRow = rows[0] || {};
      const rowLabelKey = getRowLabelKey(firstRow) || 'source';
      const columns = createCareerSiteColumns(
        rows,
        pageTypes
      );
      setCareersSiteColumns(columns);
      const tableData = rows.map((row, index) => {
        const tableRow = {
          key:
            row?.[rowLabelKey] !== undefined
              ? `${rowLabelKey}-${row[rowLabelKey]}-${index}`
              : index,

          [rowLabelKey]: row?.[rowLabelKey] ?? '-',

          total: row?.total || 0,
        };

        pageTypes.forEach((pageType) => {
          tableRow[pageType] =
            row?.counts?.[pageType] || 0;
        });

        return tableRow;
      });

      setCareersSiteData(tableData);
      const totalsRow = {
        [rowLabelKey]: 'Total',
        total: grandTotal,
      };

      pageTypes.forEach((pageType) => {
        totalsRow[pageType] =
          columnTotals?.[pageType] || 0;
      });
      setCareersSiteTotals(totalsRow);
    } catch (error) {
      console.error('Career site summary error:',error);
      setCareersSiteColumns([]);
      setCareersSiteData([]);
      setCareersSiteTotals(null);
    } finally {
      setLoading(false);
    }
  };
  const handleDateRangeChange = (values) => {
    if (!values || values.length !== 2) {
      return;
    }

    const start = values[0].startOf('month');
    const end = values[1].startOf('month');

    const normalizedRange = [start, end];

    sessionStorage.setItem(
      DATE_RANGE_STORAGE_KEY,
      JSON.stringify([
        start.format('YYYY-MM-DD'),
        end.format('YYYY-MM-DD'),
      ])
    );

    setDateRange(normalizedRange);
    fetchCareerSiteSummary(normalizedRange);
    getData(normalizedRange);
  };
  return (
    <div className="">
      {/* Date Range */}
      <div className="flex justify-start">
        <RangePicker
          picker="month"
          value={dateRange}
          onChange={handleDateRangeChange}
          format="MMM YYYY"
          allowClear={false}
        />
      </div>

      {/* Careers Site */}
      <div className="space-y-4 mt-8">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <div className="uppercase font-bold text-xl">
            YOUR CAREERS SITE
          </div>

          <Button
            type="link"
            className="px-0 font-semibold hover:underline self-start sm:self-auto"
            onClick={() => {
              let careerURL = sessionStorage.getItem('careerSiteDomain');
              if (!careerURL) return;
              window.open(careerURL, '_blank');
            }}
          >
            VIEW CAREERS SITE
          </Button>
        </div>
        <div className="w-full overflow-x-auto">
          <Table
            columns={careersSiteColumns}
            dataSource={careersSiteData}
            loading={loading}
            pagination={false}
            scroll={{ x: 'max-content' }}
            className="min-w-[320px]"
            rowClassName={(record, index) =>
              index % 2 === 0
                ? 'bg-slate-50'
                : ''
            }
            summary={() => {
              if (!careersSiteTotals) {
                return null;
              }
              return (
                <Table.Summary.Row className="font-semibold bg-slate-100">
                  {careersSiteColumns.map((column, index) => {
                    const key = column.dataIndex;
                    return (
                      <Table.Summary.Cell
                        key={column.key}
                        index={index}
                        align={column.align}
                      >
                        {index === 0
                          ? 'Total'
                          : careersSiteTotals[key] ?? ''}
                      </Table.Summary.Cell>
                    );
                  })}
                </Table.Summary.Row>
              );
            }}
          />
        </div>
      </div>
      {/* Landing Pages */}
      <div className="space-y-8 mt-10">
        {(() => {
          const pagesToRender =
            landingData && landingData.length > 0
              ? landingData
              : [
                {
                  key: 'empty-landing-page',
                  id: null,
                  landingPageURL: null,
                  analytics: {
                    report: {
                      landing: null,
                      rows: [],
                      totals: null,
                    },
                  },
                },
              ];

          return pagesToRender.map((page, idx) => {
            const isRecord = !!page.id;
            const landingURL = isRecord? page.landingPageURL: null;
            const landingLabel = isRecord? page.analytics?.report?.landing: null;
            const rows = isRecord? page.analytics?.report?.rows || []: page.data || [];
            const totals = isRecord? page.analytics?.report?.totals: null;
            const rowLabelKey =rows.length > 0? getRowLabelKey(rows[0]): 'source';
            const columns = rows.length > 0 ? createLandingReportColumns(rows, totals): [];
            const tableData = rows.map((r, i) => ({
              key: i,
              ...r,
            }));
            return (
              <div
                key={
                  isRecord
                    ? `record-${page.id}`
                    : page.key || idx
                }
                className="space-y-4"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="uppercase font-bold text-xl break-words">
                    LANDING PAGE
                    {landingLabel
                      ? ` - ${landingLabel}`
                      : ''}
                  </div>

                  <Button
                    type="link"
                    className="px-0 font-semibold hover:underline self-start sm:self-auto"
                    onClick={() => {
                      if (!landingURL) return;

                      const url = `${landingURL}`;

                      window.open(url, '_blank');
                    }}
                  >
                    VIEW LANDING PAGE
                  </Button>
                </div>

                <div className="w-full overflow-x-auto">
                  <Table
                    columns={columns}
                    dataSource={tableData}
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    className="min-w-[320px]"
                    rowClassName={(record, index) =>
                      index % 2 === 0 ? 'bg-slate-50' : ''
                    }
                    summary={() => {
                      if (!totals) return null;

                      return (
                        <Table.Summary.Row className="font-semibold bg-slate-100">
                          {columns.map((column) => {
                            const key = column.dataIndex;
                            const isLabelColumn = key === rowLabelKey;
                            return (
                              <Table.Summary.Cell
                                key={column.key}
                                index={column.key}
                                align={isLabelColumn ? 'left' : 'center'}
                                className={isLabelColumn ? 'text-left' : ''}
                                style={isLabelColumn ? { textAlign: 'left' } : undefined}
                              >
                                {isLabelColumn ? 'Total' : totals[key] ?? ''}
                              </Table.Summary.Cell>
                            );
                          })}
                        </Table.Summary.Row>
                      );
                    }}
                  />
                </div>
              </div>
            );
          });
        })()}
      </div>
    </div>
  );
};

export default LandingPage;